import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { DragOutlined, SortAscendingOutlined } from "@ant-design/icons";
import { App, Avatar } from "antd";
import $ from "jquery";
import { Observer, observer } from "mobx-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from "react";
import { Alert, FOLDER_TREE_DATA_DEFAULTS, FOLDER_TREE_TOOLBAR_INITIAL_STATE, Icon, Tree, createObserverTreeRenderItem, useFolderTree, useFolderTreeProvider, useFolderTreeProxyRefActions, useFolderTreeResponsive } from "@devowl-wp/react-folder-tree";
import { Provider as LicenseProvider } from "@devowl-wp/real-product-manager-wp-client";
import { RatingPointer } from "@devowl-wp/real-utils";
import { ProFooter } from "./proFooter.js";
import { TaxSwitcher } from "./taxSwitcher.js";
import { useHandleSelect } from "../../hooks/tree/useHandleSelect.js";
import { usePaginationFastMode } from "../../hooks/tree/usePaginationFastMode.js";
import { usePostDraggable } from "../../hooks/tree/usePostDraggable.js";
import { usePostDroppable } from "../../hooks/tree/usePostDroppable.js";
import { useSortMode } from "../../hooks/tree/useSortMode.js";
import { useToolbarState } from "../../hooks/tree/useToolbarState.js";
import { useTrashNode } from "../../hooks/tree/useTrashNode.js";
import { useStores } from "../../store/rootStore.js";
import { TreeStore } from "../../store/treeStore.js";
import { __, _i } from "../../utils/i18n.js";
import { Provider } from "../common/provider.js";
import { ConfigLicensingConsumer } from "../licensing/consumer.js";
const DEFAULT_CREATABLE_CLASSES = "page-title-action add-new-h2";
/**
 * The application tree handler for Real Category Management.
 */ const AppTreeBody = observer(({ callbacksRef, onUiBundleChange })=>{
    const { message, modal } = App.useApp();
    const { treeStore, optionStore } = useStores();
    const [, bumpSelectionRevision] = useReducer((revision)=>revision + 1, 0);
    const { selectedId } = treeStore;
    const { renameDisabled, trashDisabled, detailsDisabled } = useToolbarState(selectedId);
    const { busySettings, publicUrl, others } = optionStore;
    const { isDevLicense, pluginRcpo: { active: rcpoActive }, typenow, taxnow, taxos, blogId, showLiteNotice, isPro, showProHints, sortableTaxos } = others;
    const id = `rcl-${blogId}`; // DOM element id for the complete tree
    const { isOrderModeActive, handleOrderModifier, handleOrderClick, handleOrderCancel } = useSortMode();
    const { isSticky, isStickyHeader, isResizable, isFullWidth, style } = useFolderTreeResponsive();
    const { isToolbarBusy, isSortableDisabled, toolbarActiveButton, set, tree, staticTree, isTreeBusy } = useFolderTree();
    const isSortable = sortableTaxos[taxnow];
    useEffect(()=>{
        if (isOrderModeActive) {
            set({
                toolbarActiveButton: "order"
            });
        }
    }, [
        isOrderModeActive,
        set
    ]);
    const makeDraggable = usePostDraggable(isOrderModeActive);
    const handleSelect = useHandleSelect(toolbarActiveButton, isOrderModeActive, makeDraggable);
    const { refreshDroppable } = usePostDroppable(id, makeDraggable);
    useEffect(()=>{
        refreshDroppable();
    }, [
        tree,
        staticTree,
        isTreeBusy,
        refreshDroppable
    ]);
    const { handleTrashModifier } = useTrashNode(handleSelect);
    usePaginationFastMode(makeDraggable);
    const handleTreeNodeRender = useMemo(()=>createObserverTreeRenderItem(Observer), []);
    /**
         * MobX states should only hold serialized data so use strings for icons
         * and render it manually.
         */ const handleRenderIcon = useCallback((icon)=>/*#__PURE__*/ _jsx(Icon, {
            type: typeof icon === "string" ? icon : undefined
        }), []);
    useEffect(()=>{
        if (!others.showLicenseFormImmediate || window.Playwright) {
            return undefined;
        }
        const createdModal = modal.info({
            icon: null,
            title: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: "Real Category Management"
                    }),
                    /*#__PURE__*/ _jsx(Provider, {
                        app: {
                            style: {
                                float: "right"
                            }
                        },
                        children: /*#__PURE__*/ _jsx(Avatar, {
                            src: `${publicUrl}images/logos/real-category-library.svg`,
                            shape: "square",
                            size: 30
                        })
                    })
                ]
            }),
            width: 800,
            okButtonProps: {
                style: {
                    display: "none"
                }
            },
            cancelButtonProps: {
                style: {
                    display: "none"
                }
            },
            mask: {
                closable: false
            },
            content: /*#__PURE__*/ _jsx(LicenseProvider, {
                children: /*#__PURE__*/ _jsx(ConfigLicensingConsumer, {
                    withinModal: true
                })
            })
        });
        return ()=>createdModal.destroy();
    }, [
        modal,
        others.showLicenseFormImmediate,
        publicUrl
    ]);
    /**
         * Reload the current view (hard page reload).
         */ const handleReload = useCallback(()=>window.location.reload(), []);
    useEffect(()=>treeStore.subscribeSelectedChange(bumpSelectionRevision), [
        treeStore,
        bumpSelectionRevision
    ]);
    const handleCreatablePersist = useCallback(async (name, _node, { parentId })=>{
        await treeStore.persist({
            name,
            parent: +parentId,
            type: typenow,
            taxonomy: taxnow
        });
        const { isRatable, slug } = optionStore;
        isRatable && new RatingPointer(slug, $(".aiot-tree-headline"));
        // Debounced: immediate makeDroppable runs before React paints the new node.
        refreshDroppable();
    }, [
        treeStore,
        optionStore,
        typenow,
        taxnow,
        refreshDroppable
    ]);
    /**
         * Handle categories sorting and update the tree so the changes are visible. If sorting
         * is cancelled the old tree gets restored.
         */ const handleSort = useCallback(async ({ id, oldIndex, newIndex, parentFromId, parentToId, nextId })=>{
        await treeStore.sort({
            id: +id,
            oldIndex,
            newIndex,
            parentFromId: +parentFromId,
            parentToId: +parentToId,
            nextId: +nextId,
            request: true
        });
    }, [
        treeStore
    ]);
    /**
         * Navigate to category details.
         */ const handleDetails = useCallback(()=>{
        window.open(`term.php?taxonomy=${taxnow}&tag_ID=${treeStore.selectedId}&post_type=${typenow}`, "_blank");
    }, [
        treeStore,
        typenow,
        taxnow
    ]);
    const handleDelayedDroppable = refreshDroppable;
    const uiBundle = useMemo(()=>({
            headline: /*#__PURE__*/ _jsx("span", {
                style: {
                    paddingRight: 5
                },
                children: __("Categories")
            }),
            renameSaveText: /*#__PURE__*/ _jsx(Icon, {
                type: "save"
            }),
            renameAddText: /*#__PURE__*/ _jsx(Icon, {
                type: "save"
            }),
            noFoldersTitle: __("No category found"),
            noFoldersDescription: __("Click the button above to create a new category."),
            noSearchResult: __("No search results found"),
            isBusyHeader: false,
            creatable: {
                backButton: {
                    cssClasses: DEFAULT_CREATABLE_CLASSES,
                    label: __("Cancel")
                },
                buttons: {
                    folder: {
                        icon: /*#__PURE__*/ _jsx(Icon, {
                            type: "folder-add"
                        }),
                        cssClasses: DEFAULT_CREATABLE_CLASSES,
                        toolTipTitle: __("Click this to create a new category"),
                        toolTipText: __("To create a subcategory, simply select a category from the list and click this button."),
                        label: __("New")
                    }
                }
            },
            toolbar: {
                backButton: {
                    label: isOrderModeActive || toolbarActiveButton === "sort" ? __("Back") : __("Cancel"),
                    save: __("Save")
                },
                buttons: {
                    order: !rcpoActive && {
                        content: /*#__PURE__*/ _jsx(DragOutlined, {}),
                        toolTipTitle: __("Reorder entries"),
                        toolTipText: _i(__("Start to reorder the entries with the help of the free plugin {{strong}}Real Custom Post Order{{/strong}}."), {
                            strong: /*#__PURE__*/ _jsx("strong", {})
                        }),
                        modifier: handleOrderModifier,
                        onClick: handleOrderClick,
                        onCancel: handleOrderCancel
                    },
                    reload: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "reload"
                        }),
                        toolTipTitle: __("Refresh"),
                        toolTipText: __("Refreshes the category view."),
                        onClick: handleReload
                    },
                    rename: {
                        action: "rename",
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "edit"
                        }),
                        toolTipTitle: __("Rename"),
                        toolTipText: __("Rename the currently selected category."),
                        disabled: renameDisabled
                    },
                    trash: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "delete"
                        }),
                        toolTipTitle: __("Delete"),
                        toolTipText: __("Delete the currently selected category."),
                        disabled: trashDisabled,
                        modifier: handleTrashModifier
                    },
                    sort: {
                        content: /*#__PURE__*/ _jsx(SortAscendingOutlined, {}),
                        toolTipTitle: __("Rearrange"),
                        toolTipText: __("Change the hierarchical order of the categories."),
                        action: "sort",
                        visible: isSortable
                    },
                    details: {
                        content: /*#__PURE__*/ _jsx(Icon, {
                            type: "ellipsis"
                        }),
                        toolTipTitle: __("View and edit category"),
                        toolTipText: __("Select a category and see more details about it or edit it."),
                        disabled: detailsDisabled,
                        onClick: handleDetails
                    }
                }
            },
            multiToolbar: FOLDER_TREE_DATA_DEFAULTS.multiToolbar
        }), [
        handleDetails,
        handleOrderCancel,
        handleOrderClick,
        handleOrderModifier,
        handleReload,
        handleTrashModifier,
        isOrderModeActive,
        isSortable,
        rcpoActive,
        renameDisabled,
        trashDisabled,
        detailsDisabled,
        toolbarActiveButton,
        treeStore.selected?.properties?.type
    ]);
    const uiBundlePushKey = JSON.stringify([
        isToolbarBusy,
        toolbarActiveButton,
        rcpoActive,
        selectedId,
        renameDisabled,
        trashDisabled,
        detailsDisabled,
        isOrderModeActive,
        isSortable,
        treeStore.selected?.properties?.type
    ]);
    const uiBundlePushKeyRef = useRef(null);
    useLayoutEffect(()=>{
        if (uiBundlePushKeyRef.current === uiBundlePushKey) {
            return;
        }
        uiBundlePushKeyRef.current = uiBundlePushKey;
        onUiBundleChange(uiBundle);
        set({
            toolbar: uiBundle.toolbar
        });
    }, [
        onUiBundleChange,
        set,
        uiBundle,
        uiBundlePushKey
    ]);
    useEffect(()=>{
        set({
            isToolbarBusy: isToolbarBusy || busySettings
        });
    }, [
        busySettings,
        isToolbarBusy,
        set
    ]);
    callbacksRef.current = {
        renderItem: handleTreeNodeRender,
        renderIcon: handleRenderIcon,
        onSelect: handleSelect,
        onNodeExpand: handleDelayedDroppable,
        onSearchResult: handleDelayedDroppable,
        onTreeStructureChanged: refreshDroppable,
        onSort: handleSort,
        onSortPersistStart: ()=>message.loading(__("Category tree will be reordered soon...")),
        onSortPersistSuccess: ()=>message.success(__("Category tree is successfully reordered.")),
        onSortPersistError: (error)=>{
            const typedError = error;
            message.error(typedError.responseJSON?.message || typedError.message || __("An unknown error occurred."));
        },
        canRenameNode: (node)=>node.id !== TreeStore.ID_ALL,
        onRenamePersist: async (categoryId, inputValue)=>{
            await treeStore.byId(+categoryId).setTitle(inputValue);
            return inputValue;
        },
        onRenameStart: (inputValue)=>message.loading(__('Renaming "%s"...', inputValue)),
        onRenameSuccess: (name)=>message.success(__('"%s" successfully renamed.', name)),
        onRenameError: (error)=>message.error(error.responseJSON.message),
        prepareCreatableDraft: (_type)=>({
                $rename: true,
                $busy: false,
                icon: "folder-open"
            }),
        getCreatableSelectedId: ()=>treeStore.selectedId,
        getCreatableRootPlacementIds: ()=>[
                TreeStore.ID_ALL
            ],
        onCreatablePersist: handleCreatablePersist,
        onCreatableStart: (inputValue)=>message.loading(__('Creating "%s"...', inputValue)),
        onCreatableSuccess: (name)=>message.success(__('"%s" successfully created.', name)),
        onCreatableError: (error)=>{
            const typedError = error;
            message.error(typedError.responseJSON?.message || typedError.message || __("An unknown error occurred."));
        }
    };
    const treeLayoutProps = {
        id,
        opposite: document.getElementById("wpbody-content"),
        attr: {
            "data-type": typenow,
            "data-tax": taxnow
        },
        innerClassName: "wrap",
        theme: "wordpress",
        headerStickyAttr: {
            top: "#wpadminbar"
        },
        isSticky,
        isStickyHeader,
        isResizable,
        isFullWidth,
        style,
        isSortable,
        sortableDelay: isSortableDisabled ? 150 : 0,
        forceSortableFallback: true
    };
    return /*#__PURE__*/ _jsxs(Tree, {
        ...treeLayoutProps,
        children: [
            isDevLicense && /*#__PURE__*/ _jsx(Alert, {
                message: /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        __("Product license not for production use!"),
                        " (",
                        /*#__PURE__*/ _jsx("a", {
                            href: __("https://devowl.io/knowledge-base/license-installation-type/"),
                            rel: "noreferrer",
                            target: "_blank",
                            children: __("Learn more")
                        }),
                        ")"
                    ]
                }),
                type: "warning",
                style: {
                    marginBottom: "10px"
                }
            }),
            !isPro && showProHints && showLiteNotice && /*#__PURE__*/ _jsx(ProFooter, {
                closeable: true
            }),
            Object.keys(taxos).length > 1 && /*#__PURE__*/ _jsx("div", {
                style: {
                    margin: "2px 0px 9px 0",
                    textAlign: "right"
                },
                children: /*#__PURE__*/ _jsx(TaxSwitcher, {
                    disabled: !!toolbarActiveButton
                })
            })
        ]
    });
});
const AppTree = observer(()=>{
    const { treeStore } = useStores();
    const { tree, staticTree } = treeStore;
    const callbacksRef = useRef({});
    const proxyRefActions = useFolderTreeProxyRefActions(callbacksRef);
    const [uiBundle, setUiBundle] = useState(()=>({
            headline: undefined,
            creatable: FOLDER_TREE_DATA_DEFAULTS.creatable,
            toolbar: FOLDER_TREE_DATA_DEFAULTS.toolbar,
            multiToolbar: FOLDER_TREE_DATA_DEFAULTS.multiToolbar,
            isBusyHeader: false,
            noFoldersTitle: "",
            noFoldersDescription: "",
            noSearchResult: "",
            renameSaveText: undefined,
            renameAddText: undefined
        }));
    const handleUiBundleChange = useCallback((bundle)=>{
        setUiBundle((previous)=>previous === bundle ? previous : bundle);
    }, []);
    const [FolderTreeProvider, folderTreeCtx] = useFolderTreeProvider({
        ...FOLDER_TREE_DATA_DEFAULTS,
        ...FOLDER_TREE_TOOLBAR_INITIAL_STATE(),
        ...uiBundle,
        tree,
        staticTree,
        isTreeBusy: treeStore.busy
    }, {}, proxyRefActions, {
        observeToolbar: false,
        observeKeys: [
            "isTreeBusy"
        ],
        callbacksRef
    });
    return /*#__PURE__*/ _jsx(FolderTreeProvider, {
        value: folderTreeCtx,
        children: /*#__PURE__*/ _jsx(AppTreeBody, {
            callbacksRef: callbacksRef,
            onUiBundleChange: handleUiBundleChange
        })
    });
});
export { AppTree };
